import { useState } from "react";
import moment from "moment-timezone";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/ui/virtualizedTable";
import { useListTemplates, TaskTemplate, ListTemplatesResponse } from "@/lib/api/checklist/templates";
import { Plus, Search, Eye, Edit, Trash2, CheckSquare, Copy, Play } from "lucide-react";
import { CommonDestinationTimezone } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface TemplateListProps {
  onViewTemplate: (template: TaskTemplate) => void;
  onEditTemplate: (template: TaskTemplate) => void;
  onDeleteTemplate: (template: TaskTemplate) => void;
  onTriggerTemplate: (template: TaskTemplate | null) => void;
  onCreateTemplate: () => void;
}

export function TemplateList({
  onViewTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onTriggerTemplate,
  onCreateTemplate,
}: TemplateListProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 1000;

  const listTemplatesResult = useListTemplates({
    page,
    limit,
    search,
    active: true,
  });
  
  const isLoading = listTemplatesResult.isLoading;
  const templates = (listTemplatesResult?.data?.data || []).filter((template: ListTemplatesResponse[number]) => !/customer updated/i.test(template.name));
  const total = templates?.length || 0;

  const columnHelper = createColumnHelper<TaskTemplate>();

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: "Template Name",
      meta: { isCellClick: true },
      cell: (info) => {
        const template = info.row.original;
        return (
          <div>
            <div className="font-medium">
              {template.name}
            </div>
            {template.description && (
              <p className="text-sm text-muted-foreground truncate max-w-md">
                {template.description}
              </p>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("tasks", {
      id: "task_count",
      header: "Tasks",
      ...({ accessorFn: (row: any) => row.tasks?.length || 0 }),
      cell: (info) => {
        const count: number = info.getValue() as any;
        return (
          <div className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{count}</span>
            <span className="text-sm text-muted-foreground">
              {count === 1 ? 'task' : 'tasks'}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("active", {
      id: "status",
      header: "Status",
      cell: (info) => {
        const isActive = info.getValue();
        return (
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("created_by", {
      id: "created_by",
      header: "Created By",
      ...({ accessorFn: (row: any) => row.created_by?.name || "-" }),
      cell: (info: any) => (
        <span className="text-sm">{info.getValue() ?? ""}</span>
      ),
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Created",
      cell: (info) => {
        return moment
          .tz(info.getValue(), CommonDestinationTimezone)
          .format("MMM DD, YYYY");
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const template = row.original;
        return (
          <div className="flex gap-2 flex-row items-center">
            <Button
              size="icon"
              variant="outline"
              onClick={() => onViewTemplate(template)}
              className="h-8 w-8"
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onEditTemplate(template)}
              className="h-8 w-8"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onTriggerTemplate(template)}
              className="h-8 w-8"
              title="Trigger Template"
            >
              <Play className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[300px]"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => onTriggerTemplate({} as any)} variant="outline">
            <Play className="mr-2 h-4 w-4" />
            Activate Template
          </Button>
          <Button onClick={onCreateTemplate}>
            <Plus className="mr-2 h-4 w-4" />
            New Template
          </Button>
        </div>
      </div>

      <DataTable
        onCellClick={(row) => onViewTemplate(row as TaskTemplate)}
        data={templates}
        columns={columns as any[]}
        height="600px"
        defaultSorting={[{ id: "created_at", desc: true }]}
      />
    </div>
  );
}
